// File: frontend/src/pages/Capabilities/components/CategoryTabs.js

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import styles from './CategoryTabs.module.css';

const CategoryTabs = ({ categories, currentCategoryIndex, onCategoryChange }) => {
  const tabsRef = useRef(null);
  const activeTabRef = useRef(null);
  
  // Scroll active tab into view when it changes
  useEffect(() => {
    if (activeTabRef.current && tabsRef.current) {
      const container = tabsRef.current;
      const activeTab = activeTabRef.current;
      
      // Calculate if the active tab is visible in the current view
      const tabLeft = activeTab.offsetLeft;
      const tabRight = tabLeft + activeTab.offsetWidth;
      const containerLeft = container.scrollLeft;
      const containerRight = containerLeft + container.offsetWidth;
      
      // Only scroll if the active tab is not fully visible
      if (tabLeft < containerLeft || tabRight > containerRight) {
        // Calculate scroll position to center the active tab
        const scrollLeft = tabLeft - (container.clientWidth / 2) + (activeTab.clientWidth / 2);
        
        // Smooth scroll to the position
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [currentCategoryIndex]);

  return (
    <div className={styles.tabsContainer}>
      <div className={styles.tabsWrapper} ref={tabsRef}>
        {categories.map((category, index) => (
          <motion.button
            key={category.id}
            ref={index === currentCategoryIndex ? activeTabRef : null}
            className={`${styles.tab} ${index === currentCategoryIndex ? styles.activeTab : ''}`}
            onClick={() => onCategoryChange(index)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-selected={index === currentCategoryIndex}
            role="tab"
          >
            {category.title}
            {index === currentCategoryIndex && (
              <motion.div 
                className={styles.activeIndicator}
                layoutId="activeIndicator"
              />
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

CategoryTabs.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  currentCategoryIndex: PropTypes.number.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
};

export default CategoryTabs;